import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { ADSENSE_CLIENT, ADS_ENABLED, loadAdSense } from "../lib/ads";
import { initClarity, initGoogleAnalytics, onFirstInteraction, trackPageView } from "../lib/analytics";
import { KeepExploring } from "../components/KeepExploring";
import { CookieConsent } from "../components/CookieConsent";
import { cn } from "../lib/utils";
import { Wordmark } from "../components/Wordmark";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        name: "google-site-verification",
        content: "9v3dpcxJlbXGvJiH_LRcLv0ND_5uFrgLu0MndZN6xnc",
      },
      {
        name: "msvalidate.01",
        content: "DACF0E9E6CE26F8BF5E4116EF37507E2",
      },
      ...(ADS_ENABLED
        ? [{ name: "google-adsense-account", content: ADSENSE_CLIENT }]
        : []),
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "AutoSpecData" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "robots",
        content: "max-snippet:-1, max-image-preview:large, max-video-preview:-1",
      },
    ],
    scripts: [
      {
        // Runs before paint: hides the server-rendered consent banner for
        // visitors who already dismissed it, so no flash and no late repaint.
        children:
          "try{if(localStorage.getItem('cookie-consent-dismissed'))document.documentElement.setAttribute('data-cc','1')}catch(e){}",
      },
      // AdSense loader ships in the server-rendered HTML so crawlers and the
      // AdSense reviewer see the tag in the source, not only after hydration.
      // It stays `async`, so it never blocks first paint; individual ad units
      // still render lazily (see src/components/AdSlot.tsx).
      ...(ADS_ENABLED
        ? [
            {
              async: true,
              crossOrigin: "anonymous" as const,
              src: `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`,
            },
          ]
        : []),
    ],


    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://img.logo.dev", crossOrigin: "anonymous" },
      { rel: "icon", href: "/favicon.ico?v=3", sizes: "48x48" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg?v=3", sizes: "any" },
      { rel: "icon", type: "image/png", href: "/favicon-48.png?v=3", sizes: "48x48" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=3", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest?v=3" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (open) {
      setHidden(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (open) {
        lastScrollY.current = y;
        return;
      }
      if (y > lastScrollY.current + 8 && y > 80) {
        setHidden(true);
      } else if (y < lastScrollY.current - 8) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  const items: Array<{ label: string; render: (className: string) => ReactNode }> = [
    {
      label: "Browse",
      render: (className) => (
        <Link to="/" onClick={() => setOpen(false)} className={className}>
          Browse
        </Link>
      ),
    },
    {
      label: "Search",
      render: (className) => (
        <Link
          to="/search"
          search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
          onClick={() => setOpen(false)}
          className={className}
        >
          Search
        </Link>
      ),
    },
    {
      label: "Rankings",
      render: (className) => (
        <Link to="/best" onClick={() => setOpen(false)} className={className}>
          Rankings
        </Link>
      ),
    },
    {
      label: "Compare",
      render: (className) => (
        <Link to="/compare" onClick={() => setOpen(false)} className={className}>
          Compare
        </Link>
      ),
    },
    {
      label: "Tools",
      render: (className) => (
        <Link to="/tools" onClick={() => setOpen(false)} className={className}>
          Tools
        </Link>
      ),
    },
    {
      label: "Blog",
      render: (className) => (
        <Link to="/blog" onClick={() => setOpen(false)} className={className}>
          Blog
        </Link>
      ),
    },
    {
      label: "VIN Decoder",
      render: (className) => (
        <Link to="/vin-decoder" onClick={() => setOpen(false)} className={className}>
          VIN Decoder
        </Link>
      ),
    },
    {
      label: "Size Compare",
      render: (className) => (
        <Link
          to="/car-size-comparison"
          search={{ a: undefined, b: undefined }}
          onClick={() => setOpen(false)}
          className={className}
        >
          Size Compare
        </Link>
      ),
    },
    {
      label: "Maintenance",
      render: (className) => (
        <Link to="/car-maintenance-schedule" onClick={() => setOpen(false)} className={className}>
          Maintenance
        </Link>
      ),
    },
  ];
  const navLinks = <>{items.map((i) => <span key={i.label}>{i.render("nav-link")}</span>)}</>;


  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md transition-transform duration-300 ease-out [&_.nav-link]:font-medium [&_.nav-link]:text-muted-foreground [&_.nav-link]:transition-colors [&_.nav-link:hover]:text-foreground",
          hidden && "max-xl:-translate-y-full"
        )}
      >
        <div className="hidden bg-surface text-surface-foreground lg:block">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-1.5 text-[11px] sm:px-6">
            <span className="tracking-wide opacity-80">
              15,800+ vehicles · 2015–2027 · EPA &amp; NHTSA verified
            </span>
            <span className="tracking-wide opacity-80">Free forever · No sign-up required</span>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex min-w-0 items-center gap-2.5 font-heading text-lg font-extrabold tracking-tight text-foreground"
            >
              <span className="ink-panel flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-soft sm:h-10 sm:w-10 xl:h-9 xl:w-9">
                <svg
                  width="37"
                  height="37"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-[37px] w-[37px] text-highlight xl:h-[30px] xl:w-[30px]"
                >
                  <path d="M12 4l6.5 2.3v5.6c0 4-2.65 6.95-6.5 8.1-3.85-1.15-6.5-4.1-6.5-8.1V6.3L12 4z" strokeWidth="1.5" />
                  <path d="M8.5 12.85a3.5 3.5 0 017 0" strokeWidth="1.3" />
                  <path d="M12 12.85l2.5-2.5" strokeWidth="1.5" />
                  <circle cx="12" cy="12.85" r="1.1" fill="currentColor" stroke="none" />
                </svg>
              </span>

              <Wordmark className="min-w-0" />
            </Link>
            <nav className="hidden items-center gap-5 text-sm xl:flex">{navLinks}</nav>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-foreground shadow-soft transition-colors hover:bg-accent xl:hidden"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {open ? (
                  <>
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>
      {open && (
        <div
          id="mobile-nav"
          className="fixed inset-0 z-40 flex flex-col bg-background pt-20 xl:hidden"
        >
          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-4 pb-6 text-lg">
            {items.map((item) => (
              <div key={item.label}>
                {item.render(
                  "block rounded-xl px-3 py-3.5 text-foreground transition-colors hover:bg-accent",
                )}
              </div>
            ))}

          </nav>
        </div>
      )}
    </>
  );
}

function SiteFooter() {
  return (
    <footer className="ink-panel">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-foreground/10 xl:h-9 xl:w-9">
                <svg
                  width="37"
                  height="37"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-[37px] w-[37px] text-highlight xl:h-[30px] xl:w-[30px]"
                >
                  <path d="M12 4l6.5 2.3v5.6c0 4-2.65 6.95-6.5 8.1-3.85-1.15-6.5-4.1-6.5-8.1V6.3L12 4z" strokeWidth="1.5" />
                  <path d="M8.5 12.85a3.5 3.5 0 017 0" strokeWidth="1.3" />
                  <path d="M12 12.85l2.5-2.5" strokeWidth="1.5" />
                  <circle cx="12" cy="12.85" r="1.1" fill="currentColor" stroke="none" />
                </svg>

              </span>
              <Wordmark />
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed opacity-75">
              Free, verified vehicle specifications for every major make and model — MPG,
              engines, dimensions, towing, safety ratings and maintenance intervals, sourced
              from U.S. government datasets.
            </p>
            <p className="mt-4 text-xs opacity-60">
              Data from{" "}
              <a
                href="https://fueleconomy.gov"
                className="underline underline-offset-2 hover:opacity-100"
                target="_blank"
                rel="noopener noreferrer"
              >
                fueleconomy.gov
              </a>{" "}
              (EPA) and NHTSA vPIC / NCAP.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-xs font-semibold uppercase tracking-[0.14em] opacity-60">
              Research
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm opacity-85">
              <Link to="/" className="hover:text-highlight">Browse all makes</Link>
              <Link
                to="/search"
                search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
                className="hover:text-highlight"
              >
                Advanced spec search
              </Link>
              <Link to="/best" className="hover:text-highlight">Best MPG rankings</Link>
              <Link to="/mpg-comparison" className="hover:text-highlight">MPG comparison chart</Link>
              <Link to="/towing-capacity" className="hover:text-highlight">Towing capacity chart</Link>
              <Link to="/car-dimensions" className="hover:text-highlight">Car dimensions chart</Link>
              <Link to="/car-safety-ratings" className="hover:text-highlight">Safety ratings chart</Link>
              <Link to="/maintenance-comparison" className="hover:text-highlight">Maintenance by brand</Link>
              <Link to="/compare" className="hover:text-highlight">Compare two vehicles</Link>
            </nav>
          </div>

          <div>
            <h2 className="font-heading text-xs font-semibold uppercase tracking-[0.14em] opacity-60">
              Free tools
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm opacity-85">
              <Link to="/vin-decoder" className="hover:text-highlight">VIN decoder</Link>
              <Link to="/car-size-comparison" search={{ a: undefined, b: undefined }} className="hover:text-highlight">Car size comparison</Link>
              <Link to="/car-maintenance-schedule" className="hover:text-highlight">Maintenance schedule</Link>
              <Link to="/tools" className="hover:text-highlight">Fuel &amp; cost calculators</Link>
              <Link to="/how-much-can-my-car-tow" className="hover:text-highlight">How much can my car tow?</Link>
              <Link to="/transmission-fluid-change-interval" className="hover:text-highlight">Transmission fluid intervals</Link>
            </nav>
          </div>

          <div>
            <h2 className="font-heading text-xs font-semibold uppercase tracking-[0.14em] opacity-60">
              Company
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm opacity-85">
              <Link to="/about" className="hover:text-highlight">About</Link>
            <Link to="/blog" className="hover:text-highlight">Blog</Link>
              <Link to="/methodology" className="hover:text-highlight">Methodology</Link>
               <Link to="/reports/truck-stats" className="hover:text-highlight">Media report</Link>
              <Link to="/contact" className="hover:text-highlight">Contact</Link>
              <a
                href="mailto:contact@autospecdata.com"
                className="hover:text-highlight"
              >
                contact@autospecdata.com
              </a>
              <a
                href="mailto:info@autospecdata.com"
                className="hover:text-highlight"
              >
                info@autospecdata.com
              </a>
              <Link to="/privacy" className="hover:text-highlight">Privacy</Link>
              <Link to="/terms" className="hover:text-highlight">Terms</Link>
            </nav>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 text-xs opacity-60 sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} AutoSpecData. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const cancelGa = onFirstInteraction(() => {
      initGoogleAnalytics();
      initClarity();
      // Warm the ad stack once the visitor engages, so units below the fold
      // fill instantly without slowing the initial page load.
      loadAdSense();
    });
    trackPageView(router.state.location.href);
    const unsubscribe = router.subscribe("onResolved", (event) => {
      trackPageView(event.toLocation.href);
    });
    return () => {
      cancelGa();
      unsubscribe();
    };
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex-1">
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
        <KeepExploring />
        <SiteFooter />
        <CookieConsent />
      </div>
    </QueryClientProvider>
  );
}
